import { useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { useRouter } from '@tanstack/react-router';
import { Link } from 'react-aria-components';
import { useAdminLogin } from '../api/public-queries';
import { ApiError } from '../api/errors';
import { Wordmark } from '../components/Mark';
import { Banner } from '../ui/Banner';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { InputField } from '../ui/Input';
import { Stack } from '../ui/Stack';
import { useDocumentTitle } from '../useDocumentTitle';
import { PRODUCT_NAME } from '@tallyframe/core';

/**
 * Sign in.
 *
 * The password goes to `POST /api/auth/login`, the session arrives as an
 * HttpOnly cookie the page cannot read, and the CSRF token it returns is kept
 * in memory by the API client for the lifetime of the tab.
 */
export function LoginRoute({ onSignedIn }: { onSignedIn(): void }): ReactElement {
  useDocumentTitle('Sign in');
  const router = useRouter();
  const login = useAdminLogin();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    try {
      await login.mutateAsync(password);
    } catch (cause) {
      setError(
        ApiError.isApiError(cause)
          ? cause.message
          : `Could not reach the ${PRODUCT_NAME} server. Check that it is running.`
      );
      return;
    }
    setPassword('');
    onSignedIn();
    await router.navigate({ to: '/', replace: true });
  }

  return (
    <div className="min-h-screen flex flex-col bg-desk font-ui">
      <header className="bg-nav px-6 py-3">
        <Link
          href="/login"
          aria-label="ReportPad home"
          className="no-underline text-on-nav-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-nav-strong"
        >
          <Wordmark tone="dark" />
        </Link>
      </header>
      <main className="flex-1 grid place-items-center p-6">
        <Card className="w-full max-w-sm">
          <form onSubmit={(event) => void submit(event)}>
            <Stack gap={4}>
              <div>
                <Wordmark />
                <h1 className="sr-only">Sign in</h1>
              </div>
              <InputField
                label="Admin password"
                hint="Use the admin password for this workspace."
                type="password"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={setPassword}
                isRequired
                autoFocus
              />
              {error ? <Banner tone="danger" title={error} /> : null}
              <Button
                type="submit"
                variant="primary"
                isDisabled={password.length === 0}
                busy={login.isPending}
                busyLabel="Signing in…"
              >
                Sign in
              </Button>
            </Stack>
          </form>
        </Card>
      </main>
    </div>
  );
}
